import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import type {
  ShopLocationAddressDto,
  ShopLocationDto,
  ShopReturnToStoreStoresDto,
} from "@babackapp/private-api-client";
import { resolveCarrierLabel } from "../../utils/resolve-return-method";

export type ReturnMethodBodyProps = {
  carrier?: string | null;
  carrierTitle?: string | null;
  returnStore?: ShopReturnToStoreStoresDto;
  location: ShopLocationDto | null;
};

export function StoreMethodBody({ returnStore }: ReturnMethodBodyProps) {
  const t = useCustomText();

  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-sm text-muted-foreground">
        {returnStore?.name ?? t("returnRequests.details.returnMethod.store")}
      </p>
      {returnStore?.address && (
        <p className="text-xs text-muted-foreground/70">
          {returnStore.address.address1}
          {returnStore.address.address2 ? `, ${returnStore.address.address2}` : ""}
          {` — ${returnStore.address.city}`}
        </p>
      )}
    </div>
  );
}

/**
 * Customer-carrier return: no label and no pickup, the customer ships the parcel
 * to the shop's warehouse. The address is the whole point of the section.
 */
export function WarehouseMethodBody({ location }: ReturnMethodBodyProps) {
  const t = useCustomText();
  const address = location?.address;
  if (!address) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        {t("returnRequests.details.returnMethod.warehouse")}
      </p>
      <WarehouseAddress address={address} />
    </div>
  );
}

function WarehouseAddress({ address }: { address: ShopLocationAddressDto }) {
  const t = useCustomText();

  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground/70">
        {t("returnRequests.details.returnMethod.warehouseDescription")}
      </p>
      {address.companyName && (
        <p className="mt-1 text-sm font-medium text-foreground">{address.companyName}</p>
      )}
      <p className="text-sm text-muted-foreground">{address.address1}</p>
      {address.address2 && <p className="text-sm text-muted-foreground">{address.address2}</p>}
      <p className="text-sm text-muted-foreground">
        {`${address.zip}, ${address.city}, ${address.country_name || address.country_code}`}
      </p>
      {address.phone && (
        <p className="text-xs text-muted-foreground/70">
          {t("returnRequests.details.returnMethod.warehousePhone", { phone: address.phone })}
        </p>
      )}
    </div>
  );
}

export function CarrierMethodBody({ carrier, carrierTitle }: ReturnMethodBodyProps) {
  return (
    <p className="text-sm text-muted-foreground">
      {resolveCarrierLabel(carrierTitle, carrier)}
    </p>
  );
}
